import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type {
  MessageGroupListParams,
  MessageGroupWritePayload,
  MessagingApi,
} from '../api/messagingApi.types'

const QUERY_KEYS = {
  groupsPrefix: (pbxId: number) => ['messagingGroups', pbxId] as const,
  groups: (pbxId: number, params?: MessageGroupListParams) =>
    ['messagingGroups', pbxId, params ?? {}] as const,
  group: (groupId: number) => ['messagingGroup', groupId] as const,
  members: (groupId: number) => ['messagingGroupMembers', groupId] as const,
  extensions: (pbxId: number) => ['messagingExtensions', pbxId] as const,
  availableSmsNumbers: (pbxId: number) =>
    ['messagingAvailableSmsNumbers', pbxId] as const,
}

export const useGetMessageGroups = (
  api: MessagingApi,
  pbxId: number | undefined,
  params?: MessageGroupListParams,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.groups(pbxId ?? 0, params),
    queryFn: () => api.getMessageGroups(pbxId!, params),
    enabled: !!pbxId,
    retry: false,
  })
}

export const useGetMessageGroup = (
  api: MessagingApi,
  groupId: number | undefined,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.group(groupId ?? 0),
    queryFn: () => api.getMessageGroup(groupId!),
    enabled: !!groupId,
    retry: false,
  })
}

export const useGetMessageGroupMembers = (
  api: MessagingApi,
  groupId: number | undefined,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.members(groupId ?? 0),
    queryFn: () => api.getMessageGroupMembers(groupId!),
    enabled: !!groupId,
    retry: false,
  })
}

export const useGetExtensions = (
  api: MessagingApi,
  pbxId: number | undefined,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.extensions(pbxId ?? 0),
    queryFn: () => api.getExtensions(pbxId!),
    enabled: !!pbxId,
    retry: false,
  })
}

export const useGetAvailableSmsNumbers = (
  api: MessagingApi,
  pbxId: number | undefined,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.availableSmsNumbers(pbxId ?? 0),
    queryFn: () => api.getAvailableSmsNumbers(pbxId!),
    enabled: !!pbxId,
    retry: false,
  })
}

// `getAvailableSmsNumbers` filters out numbers already assigned to any
// group, so any mutation that adds/removes a number from a group changes
// the pool of numbers the UI considers "available" for primary/secondary
// selection. Without invalidating this query the Primary SMS dropdown and
// "Add SMS" modal stay stale — e.g. the previous primary does not reappear
// as a selectable option until the page is refreshed.
const invalidateGroupAndAvailability = (
  queryClient: ReturnType<typeof useQueryClient>,
  pbxId: number | undefined,
  groupId?: number,
) => {
  if (groupId !== undefined) {
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.group(groupId) })
  }
  if (pbxId) {
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.groupsPrefix(pbxId),
    })
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.availableSmsNumbers(pbxId),
    })
  }
}

export const useCreateMessageGroup = (
  api: MessagingApi,
  pbxId: number | undefined,
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: MessageGroupWritePayload) =>
      api.createMessageGroup(payload),
    onSuccess: () => {
      invalidateGroupAndAvailability(queryClient, pbxId)
    },
  })
}

export const useUpdateMessageGroup = (
  api: MessagingApi,
  groupId: number,
  pbxId: number | undefined,
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: Partial<MessageGroupWritePayload>) =>
      api.updateMessageGroup(groupId, payload),
    onSuccess: () => {
      invalidateGroupAndAvailability(queryClient, pbxId, groupId)
    },
  })
}

export const useDeleteMessageGroup = (
  api: MessagingApi,
  pbxId: number | undefined,
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (groupId: number) => api.deleteMessageGroup(groupId),
    onSuccess: () => {
      invalidateGroupAndAvailability(queryClient, pbxId)
    },
  })
}

export const useAddMessageGroupMember = (
  api: MessagingApi,
  groupId: number,
  pbxId: number | undefined,
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (extensionId: number) =>
      api.addMessageGroupMember(groupId, extensionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.members(groupId) })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.group(groupId) })
      if (pbxId) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.groupsPrefix(pbxId),
        })
      }
    },
  })
}

export const useRemoveMessageGroupMember = (
  api: MessagingApi,
  groupId: number,
  pbxId: number | undefined,
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (memberId: number) => api.removeMessageGroupMember(memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.members(groupId) })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.group(groupId) })
      if (pbxId) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.groupsPrefix(pbxId),
        })
      }
    },
  })
}
